import {
  Link, useLocation, useNavigate, useParams
} from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  Page,
  Spinner,
  DeleteConfirmation
} from "@whitespace/whitespace-ui";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormGroup,
  Row
} from "react-bootstrap";

import { yupResolver } from "@hookform/resolvers/yup";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { convertHoursAndMinutesToDecimal, convertToDateFromLocalDate } from "helpers/DateUtils";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import addStaffOnSiteSchema from "modules/schemas/addStaffOnSiteSchema";
import { useForm } from "react-hook-form";
import IEditStaffOnSiteRequestModel from "interfaces/Request/IEditStaffOnSiteRequestModel";

const EditStaffOnSite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hwrcSiteId, diaryDate } = useParams();
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const editDetails = { ...location.state as IEditStaffOnSiteRequestModel };
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue
  } = useForm<IEditStaffOnSiteRequestModel>({
    resolver: yupResolver(addStaffOnSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [isStartTimeAfterEndTimeError, setIsStartTimeAfterEndTimeError] = useState<boolean>(false);
  const [timeInRef, setTimeInRef] = useState<TimePickerComponent | undefined>();
  const [timeOutRef, setTimeOutRef] = useState<
  TimePickerComponent | undefined
  >();
  const [isSending, setIsSending] = useState<boolean>(false);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const {
    isSending: updateStaffOnSiteIsSending,
    errors: updateStaffOnSiteErrors,
    sendRequest: updateStaffOnSiteSendRequest,
    statusCode: updateStaffOnSiteStatusCode
  } = useAmplifyREST();

  const {
    isSending: deleteStaffOnSiteIsSending,
    errors: deleteStaffOnSiteErrors,
    sendRequest: deleteStaffOnSiteSendRequest,
    statusCode: deleteStaffOnSiteStatusCode
  } = useAmplifyREST();

  useEffect(() => {
    setIsSending(updateStaffOnSiteIsSending || deleteStaffOnSiteIsSending);
  }, [deleteStaffOnSiteIsSending, updateStaffOnSiteIsSending]);

  useEffect(() => {
    if (updateStaffOnSiteStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [updateStaffOnSiteStatusCode]);

  useEffect(() => {
    if (deleteStaffOnSiteStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [deleteStaffOnSiteStatusCode]);

  useEffect(() => {
    if (deleteStaffOnSiteStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [deleteStaffOnSiteStatusCode]);

  useEffect(() => {
    if (updateStaffOnSiteStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [updateStaffOnSiteStatusCode]);

  useEffect(() => {
    if (updateStaffOnSiteErrors) {
      setApiErrors(result => [...result, ...updateStaffOnSiteErrors]);
      setShowExceptions(true);
    }
  }, [updateStaffOnSiteErrors, setApiErrors, setShowExceptions]);

  useEffect(() => {
    if (deleteStaffOnSiteErrors) {
      setApiErrors(result => [...result, ...deleteStaffOnSiteErrors]);
      setShowExceptions(true);
    }
  }, [deleteStaffOnSiteErrors, setApiErrors, setShowExceptions]);

  const handleDeleteButtonClick = () => {
    setDeleteButtonPressed(false);
    deleteStaffOnSiteSendRequest(
      encodeURI(`hwrcsitediary/staffonSite/${editDetails.id}`),
      "DELETE"
    );
  };

  const canFormBeSubmitted = (): boolean => {
    const mandatoryFieldsEntered = getValues("name").length > 0 && timeInRef?.value != null;

    let valueHasChanged = false;

    if (editDetails != null) {
      valueHasChanged = getValues("name") !== editDetails.name
        || getValues("notes") !== (editDetails.notes ?? "")
        || getValues("cotcQualified") !== editDetails.cotcQualified
        || timeInRef?.value?.toISOString() !== editDetails.timeIn
        || timeOutRef?.value?.toISOString() !== editDetails.timeOut;
    }

    let isTimeOutBeforeTimeIn = false;
    if (timeInRef?.value && timeOutRef?.value) {
      const timeOutCompare = convertHoursAndMinutesToDecimal(timeOutRef.value.getHours(), timeOutRef.value.getMinutes());
      const timeInCompare = convertHoursAndMinutesToDecimal(
        timeInRef.value.getHours(),
        timeInRef.value.getMinutes()
      );
      isTimeOutBeforeTimeIn = timeOutCompare! <= timeInCompare!;
    }

    const canBeSubmitted = mandatoryFieldsEntered && valueHasChanged && !isTimeOutBeforeTimeIn;

    setUpdateButtonDisabled(!canBeSubmitted);
    setIsStartTimeAfterEndTimeError(isTimeOutBeforeTimeIn);

    return canBeSubmitted;
  };

  const handleStaffOnSiteSubmitHandler = (
    request: IEditStaffOnSiteRequestModel
  ) => {
    const timeIn = convertToDateFromLocalDate(diaryDate!);
    timeIn.setHours(timeInRef!.value.getHours());
    timeIn.setMinutes(timeInRef!.value.getMinutes());
    request.timeIn = timeIn.toISOString();
    if (timeOutRef && timeOutRef.value) {
      const timeOut = convertToDateFromLocalDate(diaryDate!);
      timeOut.setHours(timeOutRef!.value.getHours());
      timeOut.setMinutes(timeOutRef!.value.getMinutes());
      request.timeOut = timeOut.toISOString();
    }
    updateStaffOnSiteSendRequest(
      encodeURI(`hwrcsitediary/staffonsite/${editDetails.id}`),
      "PUT",
      request
    );
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setApiErrors([]);
    setShowExceptions(false);
  }, [setShowExceptions, setApiErrors]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />

      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="add-staff-on-site">
              Edit Staff on Site Entry
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleStaffOnSiteSubmitHandler)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    active
                    data-testid="breadcrumb-edit-staffonsite"
                  >
                    Edit Staff
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  aria-disabled={deleteStaffOnSiteIsSending}
                  aria-label="Delete staff on site"
                  type="button"
                  spin={deleteStaffOnSiteIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || isSending}
                  aria-disabled={updateButtonDisabled || isSending}
                  type="submit"
                  spin={updateStaffOnSiteIsSending}
                  data-testid="btnUpdate"
                  aria-label="update staff on site"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  aria-label="Cancel update and return to site diary"
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="staffOnSite pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mb-3 mt-3">
                      <FormGroup as={Row} controlId="staffName">
                        <Form.Label
                          id="staffNameLabel"
                          className="horiz-form-label"
                        >
                          Staff Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            data-testid="staffName"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                            defaultValue={editDetails.name}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>

                    <Row xs={12} md={12} className="mb-3 mt-3">
                      <Col>
                        <Form.Group as={Row} className="mb-3">
                          <Form.Label
                            id="timeInLabel"
                            data-testid="timeInLabel"
                            htmlFor="timeInInput"
                            column
                            sm="2"
                            className="horiz-form-label"
                          >
                            Time In:
                          </Form.Label>
                          <Col sm="4">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => {
                                setTimeInRef(t);
                              }}
                              id="timeInInput"
                              data-testid="timeIn"
                              type="text"
                              htmlAttributes={{
                                "aria-labelledby": "timeInLabel",
                                "aria-required": "true"
                              }}
                              format="HH:mm"
                              onChange={() => {
                                canFormBeSubmitted();
                              }}
                              showClearButton={false}
                              value={new Date(editDetails.timeIn!)}
                            />
                          </Col>
                          {isStartTimeAfterEndTimeError && (
                            <span
                              data-testid="timeInAfterTimeOut"
                              className="validation-error"
                            >
                              The Time In cannot be greater than the Time Out
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group as={Row}>
                          <Form.Label
                            id="timeOutLabel"
                            data-testid="timeOutLabel"
                            htmlFor="timeOutInput"
                            column
                            sm="2"
                            className="horiz-form-label"
                          >
                            Time Out:
                          </Form.Label>
                          <Col sm="4">
                            <TimePickerComponent
                              style={{ marginLeft: "2%" }}
                              ref={(t: TimePickerComponent) => {
                                setTimeOutRef(t);
                              }}
                              id="timeOutInput"
                              data-testid="timeOut"
                              type="text"
                              htmlAttributes={{
                                "aria-labelledby": "timeOutLabel",
                                "aria-required": "false"
                              }}
                              format="HH:mm"
                              onChange={() => {
                                canFormBeSubmitted();
                              }}
                              showClearButton={false}
                              value={
                                editDetails.timeOut
                                  ? new Date(editDetails.timeOut)
                                  : undefined
                              }
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="cotcQualified"
                      >
                        <Form.Label
                          id="cotcQualifiedLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          COTC Qualified:
                        </Form.Label>

                        <Form.Check
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="cotcQualifiedLabel"
                          aria-required="true"
                          {...register("cotcQualified")}
                          defaultChecked={editDetails.cotcQualified}
                          onChange={() => {
                            setValue(
                              "cotcQualified",
                              !getValues("cotcQualified")
                            );
                            canFormBeSubmitted();
                          }}
                        />

                        <ErrorMessages
                          fieldName="cotcQualified"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="notes">
                        <Form.Label
                          id="notesLabel"
                          className="horiz-form-label"
                        >
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck(
                              "notes",
                              errors,
                              null
                            )}
                            aria-labelledby="notesLabel"
                            maxLength={100}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("notes")}
                            defaultValue={editDetails.notes}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="notes"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                  </div>
                </Row>
              </Container>
            </section>
          </Form>
        </Card.Body>
      </Card>
      <DeleteConfirmation
        title="Delete Staff on Site"
        onDelete={() => {
          handleDeleteButtonClick();
        }}
        onHide={() => {
          setDeleteButtonPressed(false);
        }}
        isDeleting={deleteStaffOnSiteIsSending}
        show={deleteButtonPressed}
      >
        <p>Are you sure you want to delete this Staff on Site?</p>
      </DeleteConfirmation>
    </Page>
  );
};

export default EditStaffOnSite;
